// src/components/Person/PersonEditPage.js
// Unified full-page edit screen - same design as WizardAddPerson but single page
import React, { useState, useEffect, useMemo } from 'react';
import { calculateFiscalParts, MARITAL_STATUS_OPTIONS } from '../../utils/fiscalCalculations';
import { getFiscalConfig } from '../../utils/fiscalConfig';
import apiService from '../../api';
import IncomeDetailModal from '../Income/IncomeDetailModal';
import './PersonEditPage.css';

// Income type configuration - same as wizard
const INCOME_TYPE_CONFIG = {
  SALARIE: { label: 'Salaire', icon: 'S', color: '#3498DB', brutLabel: 'Salaire brut annuel', chargesRate: 0.23, abatementRate: 0.10, abatementMax: 14171 },
  CHOMAGE: { label: 'Chômage', icon: 'C', color: '#9B59B6', brutLabel: 'Allocation brute', chargesRate: 0, abatementRate: 0.10, abatementMax: 14171 },
  RETRAITE: { label: 'Retraite', icon: 'R', color: '#16A085', brutLabel: 'Pension brute', chargesRate: 0, abatementRate: 0.10, abatementMax: 4321 },
  EI_MICRO_BNC: { label: 'EI Micro-BNC', icon: 'EI', color: '#27AE60', brutLabel: 'Chiffre d\'affaires', chargesRate: 0.211, abatementRate: 0.34 },
  EI_MICRO_BIC_SERVICE: { label: 'EI Micro-BIC Services', icon: 'EI', color: '#2ECC71', brutLabel: 'Chiffre d\'affaires', chargesRate: 0.212, abatementRate: 0.50 },
  EI_MICRO_BIC_VENTE: { label: 'EI Micro-BIC Vente', icon: 'EI', color: '#58D68D', brutLabel: 'Chiffre d\'affaires', chargesRate: 0.123, abatementRate: 0.71 },
  EI_REEL_BNC: { label: 'EI Réel BNC', icon: 'EI', color: '#1E8449', brutLabel: 'Chiffre d\'affaires', chargesRate: null, abatementRate: null },
  EI_REEL_BIC: { label: 'EI Réel BIC', icon: 'EI', color: '#196F3D', brutLabel: 'Chiffre d\'affaires', chargesRate: null, abatementRate: null },
  LMNP_MICRO: { label: 'LMNP Micro', icon: 'LM', color: '#E67E22', brutLabel: 'Loyers bruts', chargesRate: 0, abatementRate: 0.50 },
  LMNP_REEL: { label: 'LMNP Réel', icon: 'LM', color: '#D35400', brutLabel: 'Loyers bruts', chargesRate: null, abatementRate: null },
  FONCIER_MICRO: { label: 'Foncier Micro', icon: 'F', color: '#F39C12', brutLabel: 'Loyers bruts', chargesRate: 0, abatementRate: 0.30 },
  FONCIER_REEL: { label: 'Foncier Réel', icon: 'F', color: '#D68910', brutLabel: 'Loyers bruts', chargesRate: null, abatementRate: null },
  // Société income types
  REMUNERATION_DIRIGEANT: { label: 'Dirigeant de société', icon: 'D', color: '#FFD700', brutLabel: 'Rémunération brute annuelle', chargesRate: 0.54, abatementRate: 0.10, abatementMax: 14171, isFromEntity: true },
  // Capital income types
  DIVIDENDES: { label: 'Dividendes', icon: '💰', color: '#F59E0B', brutLabel: 'Dividendes bruts', isFromEntity: true, isAutoGenerated: true },
};

const INCOME_CATEGORIES = [
  { id: 'emploi', label: 'Emploi', types: ['SALARIE', 'CHOMAGE', 'RETRAITE'] },
  { id: 'societe', label: 'Société', types: ['REMUNERATION_DIRIGEANT'] },
  { id: 'independant', label: 'EI (Indépendant)', types: ['EI_MICRO_BNC', 'EI_MICRO_BIC_SERVICE', 'EI_MICRO_BIC_VENTE', 'EI_REEL_BNC', 'EI_REEL_BIC'] },
  { id: 'immobilier', label: 'Immobilier', types: ['LMNP_MICRO', 'LMNP_REEL', 'FONCIER_MICRO', 'FONCIER_REEL'] },
  { id: 'capital', label: 'Capital', types: ['DIVIDENDES'] }
];

const PersonEditPage = ({ individualId, onClose, onSave, deletedShareholdingIds = [], standalone = false, mode = 'scenario' }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Person data
  const [personData, setPersonData] = useState({
    first_name: '',
    last_name: '',
    marital_status: 'Célibataire',
    children_count: 0,
    single_parent: false,
    fiscal_residence: 'France',
    dividend_tax_option: 'PFU'  // Legacy - kept for backwards compatibility
  });

  // Per-year capital income tax options: { 2024: 'PFU', 2025: 'BAREME', ... }
  // When changed for one year, it applies to ALL capital income for that year
  const [capitalTaxOptionsByYear, setCapitalTaxOptionsByYear] = useState({});

  // Income sources
  const [incomeSources, setIncomeSources] = useState([]);
  const [expandedSourceId, setExpandedSourceId] = useState(null);
  // Track deleted income IDs to persist deletions on save
  const [deletedIncomeIds, setDeletedIncomeIds] = useState([]);

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState({ sourceIndex: null, year: null, incomeType: null, initialData: {} });

  // Calculation breakdown popover
  const [calcPopover, setCalcPopover] = useState({ visible: false, sourceIndex: null, year: null });

  // Dividend calculation breakdown popover
  const [dividendPopover, setDividendPopover] = useState({ visible: false, year: null, dividendInfo: null });

  // Help modals for dividend columns
  const [helpModal, setHelpModal] = useState({ type: null, year: null, data: null });

  // DansMaPoche breakdown modal
  const [dmpBreakdown, setDmpBreakdown] = useState({ visible: false, data: null });

  // Load data
  useEffect(() => {
    if (!individualId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Use template API when in template mode, regular API otherwise
        const person = mode === 'template'
          ? await apiService.getIndividualTemplate(individualId)
          : await apiService.getIndividual(individualId);
        setPersonData({
          first_name: person.first_name || '',
          last_name: person.last_name || '',
          marital_status: person.marital_status || 'Célibataire',
          children_count: person.children_count || 0,
          single_parent: person.single_parent || false,
          fiscal_residence: person.fiscal_residence || 'France',
          dividend_tax_option: person.dividend_tax_option || person.dividendTaxOption || 'PFU'
        });

        // For templates, incomes come directly from the template response
        // For scenario individuals, fetch incomes separately
        const incomes = mode === 'template'
          ? (person.incomes || [])
          : await apiService.fetchIncomes(individualId);

        // Filter out incomes linked to deleted shareholdings (for instant UI update before save)
        const filteredIncomes = (incomes || []).filter(income => {
          const shareholdingId = income.shareholding_id || income.shareholdingId;
          return !shareholdingId || !deletedShareholdingIds.includes(shareholdingId);
        });

        // Process incomes - for linked incomes, fetch entity data
        const sources = await Promise.all(filteredIncomes.map(async (income) => {
          const linkedEntityId = income.linked_entity_id || income.linkedEntityId;
          const isAutoGenerated = income.is_auto_generated || income.isAutoGenerated;

          // For linked incomes, calculate amounts DYNAMICALLY from shareholding + entity
          // DIVIDENDES incomes are pre-calculated by the server, so we handle them differently
          const incomeType = income.income_type || income.incomeType;

          if (linkedEntityId && isAutoGenerated && incomeType === 'DIVIDENDES') {
            // DIVIDENDES: Pre-calculated by server, just display with entity info
            try {
              const entity = await apiService.getEntity(linkedEntityId);

              // Fetch shareholding to get dispense setting and tax option
              const shareholdingId = income.shareholding_id || income.shareholdingId;
              let acompteDispense = false;
              let shareholdingTaxOption = null;
              if (shareholdingId) {
                try {
                  const shareholding = await apiService.getShareholding(shareholdingId);
                  acompteDispense = shareholding.acompte_dispense || shareholding.acompteDispense || false;
                  shareholdingTaxOption = shareholding.dividend_tax_option || shareholding.dividendTaxOption || null;
                } catch (shErr) {
                  console.error('Error fetching shareholding for dispense:', shErr);
                }
              }

              // Use stored yearly_amounts from database
              const yearlyData = {};
              (income.yearly_amounts || []).forEach(a => {
                yearlyData[a.year] = {
                  grossAmount: a.amount || 0,
                  charges: 0,      // No charges on dividends
                  netAmount: a.amount || 0,
                  isFromEntity: true,
                  dispense: { checked: acompteDispense, date: '' }
                };
              });

              return {
                id: income.id,
                type: 'DIVIDENDES',
                label: income.label || `Dividendes ${entity.entity_name || entity.entityName}`,
                startDate: income.start_date ? income.start_date.substring(0, 10) : '',
                endDate: income.end_date ? income.end_date.substring(0, 10) : '',
                yearlyData,
                specificFields: income.specific_fields || {},
                linkedEntityId,
                linkedEntityName: entity.entity_name || entity.entityName,
                isAutoGenerated: true,
                isFromEntity: true,
                isDividend: true,
                shareholdingId,
                shareholdingTaxOption  // Pass shareholding's tax option for initialization
              };
            } catch (err) {
              console.error('Error fetching linked entity for dividends:', err);
            }
          }

          if (linkedEntityId && isAutoGenerated && incomeType !== 'DIVIDENDES') {
            // REMUNERATION_DIRIGEANT: Dynamic calculation from entity manager data
            try {
              const entity = await apiService.getEntity(linkedEntityId);
              const managers = entity.managers || [];
              const role = income.specific_fields?.role || income.specificFields?.role;
              const shareholdingId = income.shareholding_id || income.shareholdingId;

              // Find matching manager by role
              const manager = managers.find(m =>
                m.role?.toUpperCase() === role?.toUpperCase()
              );

              if (manager) {
                // DYNAMIC DATES: Use manager's salaryStartDate from entity (when company starts paying)
                let incomeStart, incomeEnd;

                // Income starts when company starts paying this manager's salary
                const salaryStartDate = manager.salaryStartDate || manager.salary_start_date ||
                                        manager.entryDate || manager.entry_date ||
                                        entity.creation_date || entity.creationDate;
                incomeStart = new Date(salaryStartDate);

                // Get shareholding for exit date only
                if (shareholdingId) {
                  try {
                    const shareholding = await apiService.getShareholding(shareholdingId);

                    // If shareholding has exit date, use it as end limit
                    const exitDateStr = shareholding.exit_date || shareholding.exitDate;
                    if (exitDateStr) {
                      incomeEnd = new Date(exitDateStr);
                    }
                  } catch (err) {
                    console.warn('[PersonEditPage] Could not fetch shareholding for exit date');
                  }
                }
                // No else needed - incomeStart already set from salaryStartDate

                // Get end date from entity forecast (always dynamic)
                const entityForecastEnd = entity.forecast_end_date || entity.forecastEndDate;
                const entityEndDate = entityForecastEnd ? new Date(entityForecastEnd) : new Date();

                // Use earlier of: shareholding exit date OR entity forecast end
                if (!incomeEnd || incomeEnd > entityEndDate) {
                  incomeEnd = entityEndDate;
                }

                // Calculate CIVIL YEARS (Jan 1 - Dec 31) for individual tax purposes
                const startYear = incomeStart.getFullYear();
                const endYear = incomeEnd.getFullYear();

                // Helper to calculate days in a year
                const daysInYear = (year) => ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 366 : 365;

                // Build entity exercice date ranges (same logic as EntityEditPage.js)
                // IMPORTANT: Clip exercice dates by forecast dates since NET is calculated for effective period
                const buildExerciceDateRanges = () => {
                  const fiscalYearEnd = entity.fiscal_year_end || entity.fiscalYearEnd || '31/12';
                  const parts = fiscalYearEnd.split('/');
                  const fyDay = parts.length === 2 ? parseInt(parts[0]) : 31;
                  const fyMonth = parts.length === 2 ? parseInt(parts[1]) : 12;

                  const creationDate = entity.creation_date || entity.creationDate;
                  const firstExerciceType = entity.first_exercice_type || entity.firstExerciceType || 'long';

                  // Get forecast dates for clipping
                  const forecastStartDate = entity.forecast_start_date || entity.forecastStartDate;
                  const forecastEndDate = entity.forecast_end_date || entity.forecastEndDate;
                  const forecastStart = forecastStartDate ? new Date(forecastStartDate) : null;
                  const forecastEnd = forecastEndDate ? new Date(forecastEndDate) : null;

                  const exerciceRanges = [];
                  const entityStart = creationDate ? new Date(creationDate) : incomeStart;

                  // Calculate first exercice end
                  let firstExerciceEndYear = entityStart.getFullYear();
                  let nextFYEnd = new Date(firstExerciceEndYear, fyMonth - 1, fyDay);
                  if (nextFYEnd <= entityStart) {
                    nextFYEnd = new Date(firstExerciceEndYear + 1, fyMonth - 1, fyDay);
                  }

                  // Check if creation date is aligned with fiscal year start
                  // Fiscal year START is the day after fiscal year END
                  const fyStartDay = fyDay === 31 && fyMonth === 12 ? 1 : fyDay + 1;
                  const fyStartMonth = fyDay === 31 && fyMonth === 12 ? 1 : (fyDay >= 28 ? fyMonth + 1 : fyMonth);
                  const isAlignedWithFiscalYear =
                    entityStart.getDate() === fyStartDay &&
                    (entityStart.getMonth() + 1) === fyStartMonth;

                  // Apply short/long logic ONLY if NOT aligned with fiscal year
                  // When aligned (e.g., Jan 1 creation with Dec 31 fiscal year end), always use normal 12-month exercices
                  let firstExerciceEnd;
                  if (isAlignedWithFiscalYear) {
                    firstExerciceEnd = nextFYEnd;
                  } else {
                    firstExerciceEnd = firstExerciceType === 'long'
                      ? new Date(nextFYEnd.getFullYear() + 1, fyMonth - 1, fyDay)
                      : nextFYEnd;
                  }

                  // Build exercice ranges with EFFECTIVE dates (clipped by forecast)
                  let currentStart = entityStart;
                  let currentEnd = firstExerciceEnd;

                  for (let i = 0; i < 10 && currentStart <= incomeEnd; i++) {
                    const exerciceKey = String(currentEnd.getFullYear());

                    // Clip to forecast period (same as EntityEditPage)
                    const effectiveStart = (forecastStart && currentStart < forecastStart) ? forecastStart : currentStart;
                    const effectiveEnd = (forecastEnd && currentEnd > forecastEnd) ? forecastEnd : currentEnd;

                    // Only add if there's a valid range after clipping
                    if (effectiveStart <= effectiveEnd) {
                      exerciceRanges.push({
                        key: exerciceKey,
                        start: new Date(effectiveStart),
                        end: new Date(effectiveEnd)
                      });
                    }

                    // Move to next exercice
                    currentStart = new Date(currentEnd);
                    currentStart.setDate(currentStart.getDate() + 1);
                    currentEnd = new Date(currentEnd.getFullYear() + 1, fyMonth - 1, fyDay);
                  }

                  return exerciceRanges;
                };

                const exerciceRanges = buildExerciceDateRanges();

                // IMPORTANT: Limit endYear to the last exercice that has data
                // This handles the 10 exercice limit - don't show years beyond what Entity has calculated
                let effectiveEndYear = endYear;
                if (exerciceRanges.length > 0) {
                  const lastExerciceEndYear = exerciceRanges[exerciceRanges.length - 1].end.getFullYear();
                  if (lastExerciceEndYear < endYear) {
                    effectiveEndYear = lastExerciceEndYear;
                    console.log(`[PersonEditPage] Limiting display to ${effectiveEndYear} (Entity has ${exerciceRanges.length} exercices, limit reached)`);
                  }
                }

                // Helper to get calculated manager data for a specific exercice key
                // Reads from managersCalculated which contains pre-calculated BRUT, NET, charges
                // Falls back to calculating from managers array if managersCalculated not present
                const getCalculatedManagerForExercice = (exerciceKey) => {
                  const exerciceData = entity.exercice_data || entity.exerciceData || {};
                  const yearData = exerciceData[exerciceKey] || {};
                  const managersCalc = yearData.managersCalculated;
                  if (managersCalc && managersCalc.length > 0) {
                    const calcManager = managersCalc.find(m =>
                      m.role?.toUpperCase() === role?.toUpperCase()
                    );
                    if (calcManager) {
                      return calcManager;
                    }
                  }

                  // Fallback: use managers array directly when managersCalculated not available
                  // The stored value (netMonthly/net_monthly) is actually BRUT from the wizard
                  if (manager) {
                    const brutMonthly = manager.netMonthly || manager.net_monthly || 0;
                    const months = manager.months || 12;
                    const annualBrut = brutMonthly * months;
                    // Net comes from entity calculation - for assimilé-salarié: net ≈ brut × 0.78
                    const annualNet = Math.round(annualBrut * 0.78);
                    return {
                      role: role,
                      brut: annualBrut,
                      net: annualNet
                    };
                  }
                  return null; // No calculated data
                };

                // Build yearlyData by CIVIL YEAR, properly handling fiscal year boundaries
                const yearlyData = {};
                const msPerDay = 24 * 60 * 60 * 1000;

                for (let year = startYear; year <= effectiveEndYear; year++) {
                  const civilYearStart = new Date(year, 0, 1);  // Jan 1
                  const civilYearEnd = new Date(year, 11, 31); // Dec 31

                  // Clip to income period
                  const periodStart = incomeStart > civilYearStart ? incomeStart : civilYearStart;
                  const periodEnd = incomeEnd < civilYearEnd ? incomeEnd : civilYearEnd;

                  if (periodStart > periodEnd) {
                    yearlyData[year] = { netAmount: 0, taxable: 0, isFromEntity: true, prorata: 0 };
                    continue;
                  }

                  // Calculate total amounts from all overlapping exercices
                  let totalNetAmount = 0;
                  let totalBrutAmount = 0;
                  const totalDays = Math.round((periodEnd - periodStart) / msPerDay) + 1;
                  const calculationDetails = []; // Store breakdown for display

                  for (const exercice of exerciceRanges) {
                    // Find overlap between this exercice and the civil year period
                    const overlapStart = exercice.start > periodStart ? exercice.start : periodStart;
                    const overlapEnd = exercice.end < periodEnd ? exercice.end : periodEnd;

                    if (overlapStart > overlapEnd) continue; // No overlap

                    const overlapDays = Math.round((overlapEnd - overlapStart) / msPerDay) + 1;
                    const exerciceTotalDays = Math.round((exercice.end - exercice.start) / msPerDay) + 1;

                    // Get pre-calculated values from entity (already includes clipping, duration factor, ACRE, etc.)
                    const calcManager = getCalculatedManagerForExercice(exercice.key);
                    if (calcManager && calcManager.net) {
                      // Prorate the exercice amounts to the overlap period
                      const netForPeriod = Math.round(calcManager.net * overlapDays / exerciceTotalDays);
                      const brutForPeriod = Math.round((calcManager.brut || 0) * overlapDays / exerciceTotalDays);
                      totalNetAmount += netForPeriod;
                      totalBrutAmount += brutForPeriod;

                      // Store calculation detail for this exercice
                      calculationDetails.push({
                        exerciceKey: exercice.key,
                        exerciceNet: calcManager.net,
                        exerciceBrut: calcManager.brut || 0,
                        overlapDays,
                        exerciceTotalDays,
                        netForPeriod,
                        brutForPeriod,
                        formulaBrut: `${(calcManager.brut || 0).toLocaleString('fr-FR')}€ × ${overlapDays}j/${exerciceTotalDays}j = ${brutForPeriod.toLocaleString('fr-FR')}€`,
                        formulaNet: `${calcManager.net.toLocaleString('fr-FR')}€ × ${overlapDays}j/${exerciceTotalDays}j = ${netForPeriod.toLocaleString('fr-FR')}€`
                      });
                    } else {
                      console.warn(`[PersonEditPage] No calculated manager data for exercice ${exercice.key} - save entity first`);
                    }
                  }

                  const prorata = Math.round((totalDays / daysInYear(year)) * 100);
                  const chargesSalariales = totalBrutAmount - totalNetAmount;

                  yearlyData[year] = {
                    grossAmount: totalBrutAmount,        // Brut annuel (pré-rempli depuis entité)
                    charges: chargesSalariales,         // Charges salariales calculées
                    netAmount: totalNetAmount,          // Net perçu (pré-rempli depuis entité)
                    // taxable sera calculé à l'affichage selon le type d'abattement choisi
                    isFromEntity: true,
                    prorata,
                    calculationDetails // Store for display
                  };
                }

                // Format dates for display (using dynamic values)
                // Use effectiveEndYear if we hit the 10 exercice limit
                const startDateDisplay = incomeStart.toISOString().split('T')[0];
                const effectiveEndDate = effectiveEndYear < endYear
                  ? new Date(effectiveEndYear, 11, 31) // Dec 31 of last exercice year
                  : incomeEnd;
                const endDateDisplay = effectiveEndDate.toISOString().split('T')[0];

                return {
                  id: income.id,
                  type: income.income_type || 'REMUNERATION_DIRIGEANT',
                  label: income.label || '',
                  startDate: startDateDisplay,
                  endDate: endDateDisplay,
                  yearlyData,
                  specificFields: income.specific_fields || {},
                  linkedEntityId,
                  linkedEntityName: entity.entity_name || entity.entityName,
                  isAutoGenerated: true,
                  isFromEntity: true,  // Brut/Net come from entity (read-only), but abatement is customizable
                  isDynamic: true      // Flag to indicate dates are dynamically fetched
                };
              }
            } catch (err) {
              console.error('Error fetching linked entity:', err);
            }
          }

          // Regular income - use stored yearly_amounts
          const yearlyData = {};
          (income.yearly_amounts || []).forEach(a => {
            yearlyData[a.year] = { grossAmount: a.amount || 0, charges: 0, taxable: 0 };
          });

          return {
            id: income.id,
            type: income.income_type || 'SALARIE',
            label: income.label || '',
            startDate: income.start_date ? income.start_date.substring(0, 10) : '',
            endDate: income.end_date ? income.end_date.substring(0, 10) : '',
            yearlyData,
            specificFields: income.specific_fields || {}
          };
        }));

        // Initialize capitalTaxOptionsByYear from dividend incomes
        const taxOptionsByYear = {};
        sources.forEach(source => {
          if (source.isDividend && source.shareholdingTaxOption && source.yearlyData) {
            Object.keys(source.yearlyData).forEach(year => {
              // Only set if not already set (first shareholding wins)
              if (!taxOptionsByYear[year]) {
                taxOptionsByYear[year] = source.shareholdingTaxOption;
              }
            });
          }
        });
        if (Object.keys(taxOptionsByYear).length > 0) {
          setCapitalTaxOptionsByYear(taxOptionsByYear);
        }

        setIncomeSources(sources);
        if (sources.length > 0) setExpandedSourceId(sources[0].id);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // Using JSON.stringify to avoid re-running when array reference changes but content is same
  }, [individualId, mode, JSON.stringify(deletedShareholdingIds)]);

  // Helpers
  const handlePersonChange = (field, value) => {
    setPersonData(prev => ({ ...prev, [field]: value }));
  };

  const handleChildrenChange = (delta) => {
    setPersonData(prev => ({ ...prev, children_count: Math.max(0, prev.children_count + delta) }));
  };

  // Get tax option for a specific year (per-year override or global default)
  const getTaxOptionForYear = (year) => {
    return capitalTaxOptionsByYear[year] || personData.dividend_tax_option || 'PFU';
  };

  // Update tax option for a year (affects ALL capital income for that year)
  // Also persists to shareholding for dividend incomes
  const updateTaxOptionForYear = async (year, option) => {
    // Update local state immediately
    setCapitalTaxOptionsByYear(prev => ({
      ...prev,
      [year]: option
    }));

    // Persist to shareholding(s) for dividend incomes
    // Find all dividend incomes with shareholding IDs
    const dividendSources = incomeSources.filter(s => s.isDividend && s.shareholdingId);
    const uniqueShareholdingIds = [...new Set(dividendSources.map(s => s.shareholdingId))];

    // Update each shareholding's dividend_tax_option
    for (const shareholdingId of uniqueShareholdingIds) {
      try {
        await apiService.updateShareholding(shareholdingId, {
          dividend_tax_option: option
        });
        console.log(`[PersonEditPage] Updated shareholding ${shareholdingId} tax option to ${option}`);
      } catch (err) {
        console.error(`[PersonEditPage] Failed to update shareholding ${shareholdingId}:`, err);
      }
    }
  };

  // Update dispense for a specific income source and year
  // Also persists to shareholding for dividend incomes
  const updateDispenseForYear = async (sourceIndex, year, dispenseData) => {
    // Update local state immediately
    setIncomeSources(prev => {
      const updated = [...prev];
      const source = { ...updated[sourceIndex] };
      source.yearlyData = { ...source.yearlyData };
      source.yearlyData[year] = {
        ...(source.yearlyData[year] || {}),
        dispense: dispenseData
      };
      updated[sourceIndex] = source;
      return updated;
    });

    // Persist to shareholding if this is a dividend income
    const source = incomeSources[sourceIndex];
    if (source?.isDividend && source?.shareholdingId) {
      try {
        await apiService.updateShareholding(source.shareholdingId, {
          acompte_dispense: dispenseData.checked
        });
        console.log(`[PersonEditPage] Updated shareholding ${source.shareholdingId} dispense to ${dispenseData.checked}`);
      } catch (err) {
        console.error(`[PersonEditPage] Failed to update shareholding dispense:`, err);
      }
    }
  };

  const fiscalParts = useMemo(() => calculateFiscalParts(personData), [personData]);
  const showSingleParent = ['Célibataire', 'Divorcé', 'Veuf'].includes(personData.marital_status) && personData.children_count > 0;

  // Income helpers
  const addIncomeSource = (incomeType) => {
    const newSource = {
      id: `new-${Date.now()}`,
      type: incomeType,
      label: '',
      startDate: '',
      endDate: '',
      yearlyData: {},
      specificFields: {},
      isNew: true
    };
    setIncomeSources(prev => [...prev, newSource]);
    setExpandedSourceId(newSource.id);
  };

  const updateIncomeSource = (index, updates) => {
    setIncomeSources(prev => {
      const newSources = prev.map((src, i) => i === index ? { ...src, ...updates } : src);
      const updatedSource = newSources[index];
      if (updatedSource && (updates.startDate !== undefined || updates.endDate !== undefined)) {
        const hasValidDates = updatedSource.startDate && updatedSource.endDate &&
          updatedSource.startDate.length >= 10 && updatedSource.endDate.length >= 10;
        if (hasValidDates) setExpandedSourceId(updatedSource.id);
      }
      return newSources;
    });
  };

  const removeIncomeSource = (index) => {
    const source = incomeSources[index];
    // Track deleted income IDs for existing incomes (those with real DB IDs)
    // Don't track auto-generated incomes (managed by server) or new incomes (not in DB yet)
    if (source?.id && !source.isNew && !source.isAutoGenerated && typeof source.id === 'number') {
      setDeletedIncomeIds(prev => [...prev, source.id]);
    }
    setIncomeSources(prev => prev.filter((_, i) => i !== index));
  };

  const getYearsForSource = (source) => {
    if (!source.startDate || !source.endDate) return [];
    const startYear = parseInt(source.startDate.split('-')[0]);
    const endYear = parseInt(source.endDate.split('-')[0]);
    if (isNaN(startYear) || isNaN(endYear)) return [];
    const years = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);
    return years;
  };

  const sourceHasValidDates = (source) => source.startDate && source.endDate && source.startDate.length >= 10 && source.endDate.length >= 10;

  const updateYearlyData = (sourceIndex, year, data) => {
    setIncomeSources(prev => prev.map((src, i) => {
      if (i !== sourceIndex) return src;
      return { ...src, yearlyData: { ...src.yearlyData, [year]: { ...src.yearlyData?.[year], ...data } } };
    }));
  };

  const coveredYears = useMemo(() => {
    const years = new Set();
    incomeSources.forEach(source => getYearsForSource(source).forEach(y => years.add(y)));
    return Array.from(years).sort((a, b) => a - b);
  }, [incomeSources]);

  const calculateYearTotals = (year) => {
    let totalNet = 0, totalTaxable = 0;
    const yearTaxOption = getTaxOptionForYear(parseInt(year));

    incomeSources.forEach(source => {
      const config = INCOME_TYPE_CONFIG[source.type];
      const yearData = source.yearlyData?.[year] || {};
      const isFromEntity = source.isReadOnly || yearData.isFromEntity;
      const isDividend = source.type === 'DIVIDENDES' || source.isDividend;

      let net, taxable;

      // Special handling for dividends
      if (isDividend) {
        const brut = yearData.grossAmount || yearData.netAmount || 0;
        net = brut;  // Dividends: brut = net (no charges)

        // In PFU mode: dividends are taxed separately (30% flat), NOT included in progressive IR
        // In Barème mode: dividends are included with 40% abatement
        if (yearTaxOption === 'PFU') {
          taxable = 0;  // Don't include in IR calculation - taxed separately at 30%
        } else {
          // Barème: 40% abatement, only 60% is taxable
          taxable = Math.round(brut * 0.60);
        }
      } else if (isFromEntity && yearData.netAmount !== undefined) {
        // Entity-linked income (non-dividend): NET is pre-calculated
        net = yearData.netAmount || 0;
        // Apply abatement for taxable income
        if (config?.abatementRate !== null) {
          const abatement = config?.abatementMax ? Math.min(net * config.abatementRate, config.abatementMax) : net * (config?.abatementRate || 0);
          taxable = Math.round(net - abatement);
        } else {
          taxable = net;
        }
      } else {
        // Manual income: calculate from gross
        const gross = yearData.grossAmount || 0;
        const charges = config?.chargesRate !== null ? Math.round(gross * (config?.chargesRate || 0)) : (yearData.charges || 0);
        net = gross - charges;
        if (config?.abatementRate !== null) {
          const abatement = config?.abatementMax ? Math.min(gross * config.abatementRate, config.abatementMax) : gross * (config?.abatementRate || 0);
          taxable = Math.round(gross - abatement);
        } else {
          taxable = Math.max(0, net);
        }
      }

      totalNet += net;
      totalTaxable += taxable;
    });
    return { totalNet, totalTaxable };
  };

  const calculateIRForYear = (year, totalTaxable, parts) => {
    const config = getFiscalConfig(year);
    if (!config) return { ir: 0, tmi: 0 };
    const quotient = totalTaxable / parts;
    let taxOnQuotient = 0;
    let tmi = 0;
    for (const bracket of config.irBrackets) {
      if (quotient > bracket.min) {
        taxOnQuotient += (Math.min(quotient, bracket.max) - bracket.min) * bracket.rate;
        if (bracket.rate > 0) tmi = bracket.rate;
      }
    }
    let ir = Math.round(taxOnQuotient * parts);
    const isCouple = ['Marié', 'Pacsé'].includes(personData.marital_status);
    const decoteThreshold = isCouple ? config.decote.coupleThreshold : config.decote.singleThreshold;
    const decoteCoef = isCouple ? config.decote.coupleCoef : config.decote.singleCoef;
    if (ir > 0 && ir < decoteThreshold) {
      ir = Math.max(0, Math.round(ir - (decoteCoef - ir * 0.4525)));
    }
    return { ir, tmi: Math.round(tmi * 100) };
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount || 0);

  // Comprehensive DansMaPoche calculation for a year
  // Properly handles PFU vs Barème for dividends
  const calculateDansMaPocheForYear = (year) => {
    const yearTaxOption = getTaxOptionForYear(parseInt(year));
    const PS_RATE = 0.172;
    const PFU_IR_RATE = 0.128;

    // Separate income types
    let regularNet = 0;       // Net from regular income (salaries, etc.)
    let regularTaxable = 0;   // Taxable from regular income
    let dividendBrut = 0;     // Total dividend brut
    const incomeDetails = []; // Detailed breakdown per source

    incomeSources.forEach(source => {
      const config = INCOME_TYPE_CONFIG[source.type];
      const yearData = source.yearlyData?.[year] || {};
      const years = getYearsForSource(source);
      if (!years.includes(parseInt(year))) return;

      const isFromEntity = source.isReadOnly || yearData.isFromEntity;
      const isDividend = source.type === 'DIVIDENDES' || source.isDividend;

      if (isDividend) {
        const brut = yearData.grossAmount || yearData.netAmount || 0;
        dividendBrut += brut;
        incomeDetails.push({
          type: 'dividend',
          label: source.label || config?.label || 'Dividendes',
          brut,
          linkedEntity: source.linkedEntityName
        });
      } else {
        let net, taxable;
        if (isFromEntity && yearData.netAmount !== undefined) {
          net = yearData.netAmount || 0;
          const brut = yearData.grossAmount || net;
          if (config?.abatementRate !== null) {
            const abatement = config?.abatementMax
              ? Math.min(net * config.abatementRate, config.abatementMax)
              : net * (config?.abatementRate || 0);
            taxable = Math.round(net - abatement);
          } else {
            taxable = net;
          }
          incomeDetails.push({
            type: 'regular',
            label: source.label || config?.label || 'Revenu',
            brut,
            net,
            taxable,
            linkedEntity: source.linkedEntityName
          });
        } else {
          const gross = yearData.grossAmount || 0;
          const charges = config?.chargesRate !== null
            ? Math.round(gross * (config?.chargesRate || 0))
            : (yearData.charges || 0);
          net = gross - charges;
          if (config?.abatementRate !== null) {
            const abatement = config?.abatementMax
              ? Math.min(gross * config.abatementRate, config.abatementMax)
              : gross * (config?.abatementRate || 0);
            taxable = Math.round(gross - abatement);
          } else {
            taxable = Math.max(0, net);
          }
          incomeDetails.push({
            type: 'regular',
            label: source.label || config?.label || 'Revenu',
            brut: gross,
            charges,
            net,
            taxable
          });
        }
        regularNet += net;
        regularTaxable += taxable;
      }
    });

    // Calculate dividend taxation based on option
    let dividendTax = 0;
    let dividendPS = 0;
    let dividendIR = 0;
    let dividendNet = 0;
    let dividendTaxable = 0;

    if (dividendBrut > 0) {
      dividendPS = Math.round(dividendBrut * PS_RATE);

      if (yearTaxOption === 'PFU') {
        // PFU: 30% flat tax (12.8% IR + 17.2% PS)
        dividendIR = Math.round(dividendBrut * PFU_IR_RATE);
        dividendTax = dividendIR + dividendPS;
        dividendNet = dividendBrut - dividendTax;
        dividendTaxable = 0; // Not included in progressive IR
      } else {
        // Barème: 40% abatement, integrated into progressive IR
        dividendTaxable = Math.round(dividendBrut * 0.60);
        // IR will be calculated on combined taxable below
        dividendNet = dividendBrut - dividendPS; // PS always deducted, IR via progressive
      }
    }

    // Calculate progressive IR
    // In PFU mode: only on regular income
    // In Barème mode: on regular income + dividend taxable (after 40% abatement)
    const totalTaxableForIR = yearTaxOption === 'PFU'
      ? regularTaxable
      : regularTaxable + dividendTaxable;

    const { ir: progressiveIR, tmi } = calculateIRForYear(year, totalTaxableForIR, fiscalParts);

    // In Barème mode, we need to estimate how much of the progressive IR is from dividends
    let dividendIRBareme = 0;
    if (yearTaxOption === 'BAREME' && dividendTaxable > 0) {
      const tmiRate = tmi / 100;
      dividendIRBareme = Math.round(dividendTaxable * tmiRate);
      dividendIR = dividendIRBareme;
      dividendTax = dividendIR + dividendPS;
      dividendNet = dividendBrut - dividendTax;
    }

    // Calculate final amounts
    const totalNet = regularNet + dividendBrut;
    const totalTax = yearTaxOption === 'PFU'
      ? progressiveIR + dividendTax  // Progressive IR on regular + flat tax on dividends
      : progressiveIR + dividendPS;   // Progressive IR includes dividends, add PS separately

    const dansMaPoche = totalNet - totalTax;

    return {
      year,
      taxOption: yearTaxOption,
      fiscalParts,
      tmi,

      // Regular income
      regular: {
        net: regularNet,
        taxable: regularTaxable,
        ir: yearTaxOption === 'PFU' ? progressiveIR : Math.round(progressiveIR - dividendIRBareme)
      },

      // Dividend income
      dividend: {
        brut: dividendBrut,
        taxable: dividendTaxable,
        ps: dividendPS,
        ir: dividendIR,
        totalTax: dividendTax,
        net: dividendNet
      },

      // Totals
      totals: {
        net: totalNet,
        tax: totalTax,
        dansMaPoche
      },

      // Detailed breakdown per source
      incomeDetails
    };
  };

  // Get dividend incomes grouped by tax year
  const dividendIncomes = useMemo(() => {
    return incomeSources.filter(s => s.type === 'DIVIDENDES' || s.isDividend);
  }, [incomeSources]);

  // Calculate dividend taxation for a specific year
  const calculateDividendTax = (year, amount, taxOption, tmi) => {
    const PS_RATE = 0.172;  // 17.2% prélèvements sociaux
    const PFU_IR_RATE = 0.128;  // 12.8% IR forfaitaire
    const ABATEMENT_RATE = 0.40;  // 40% abattement pour barème

    // Acompte at payment time: always 30% (12.8% IR + 17.2% PS)
    // Unless dispense requested (only IR part is dispensed)
    const acompteIR = Math.round(amount * PFU_IR_RATE);
    const acomptePS = Math.round(amount * PS_RATE);
    const totalAcompte = acompteIR + acomptePS;

    if (taxOption === 'PFU') {
      // PFU: 30% flat tax, no regularization
      return {
        brut: amount,
        acompteIR,
        acomptePS,
        totalAcompte,
        finalIR: acompteIR,
        finalPS: acomptePS,
        regulIR: 0,  // No change
        netApresImpot: amount - totalAcompte,
        taxOption: 'PFU',
        details: {
          description: 'Prélèvement Forfaitaire Unique (Flat Tax)',
          irRate: '12,8%',
          psRate: '17,2%',
          totalRate: '30%',
          abatement: 0
        }
      };
    } else {
      // BAREME: 40% abatement + progressive IR + 17.2% PS
      const baseImposable = Math.round(amount * (1 - ABATEMENT_RATE));  // 60% of amount
      const abatement = amount - baseImposable;
      const tmiRate = (tmi || 30) / 100;
      const finalIR = Math.round(baseImposable * tmiRate);
      const finalPS = acomptePS;  // PS unchanged
      const regulIR = finalIR - acompteIR;  // Can be positive (more tax) or negative (refund)

      return {
        brut: amount,
        acompteIR,
        acomptePS,
        totalAcompte,
        finalIR,
        finalPS,
        regulIR,
        netApresImpot: amount - finalIR - finalPS,
        taxOption: 'BAREME',
        details: {
          description: 'Barème progressif avec abattement 40%',
          abatement,
          baseImposable,
          tmi: tmi || 30,
          irFormula: `${baseImposable.toLocaleString('fr-FR')}€ × ${tmi || 30}% = ${finalIR.toLocaleString('fr-FR')}€`
        }
      };
    }
  };

  // Helper: Convert income source to API format
  const incomeSourceToApiFormat = (source) => {
    // Build yearly_amounts array from yearlyData
    const yearly_amounts = Object.entries(source.yearlyData || {}).map(([year, data]) => ({
      year: parseInt(year),
      amount: data.grossAmount || 0,
      manual_charges: data.manualCharges || null
    }));

    return {
      income_type: source.type,
      label: source.label || '',
      start_date: source.startDate || null,
      end_date: source.endDate || null,
      specific_fields: source.specificFields || {},
      yearly_amounts
    };
  };

  // Save handler
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveSuccess(false);

    try {
      // 1. Save personal data
      if (mode === 'template') {
        // For templates, include incomes in the update payload
        const incomes = incomeSources
          .filter(src => !src.isAutoGenerated) // Don't save auto-generated incomes
          .map(incomeSourceToApiFormat);

        await apiService.updateIndividualTemplate(individualId, {
          ...personData,
          incomes,
          deleted_income_ids: deletedIncomeIds
        });
      } else {
        // For scenario individuals, update personal data first
        await apiService.updateIndividual(individualId, personData);

        // 2. Delete removed incomes
        for (const incomeId of deletedIncomeIds) {
          try {
            await apiService.deleteIncome(incomeId);
            console.log(`[PersonEditPage] Deleted income ${incomeId}`);
          } catch (err) {
            console.error(`[PersonEditPage] Failed to delete income ${incomeId}:`, err);
          }
        }

        // 3. Save income changes (create new, update existing)
        for (const source of incomeSources) {
          // Skip auto-generated incomes (managed by server via entity/shareholding)
          if (source.isAutoGenerated) continue;

          const apiData = incomeSourceToApiFormat(source);

          if (source.isNew) {
            // Create new income
            try {
              await apiService.createIncome(individualId, apiData);
            } catch (err) {
              console.error('[PersonEditPage] Failed to create income:', err);
            }
          } else if (typeof source.id === 'number') {
            // Update existing income
            try {
              await apiService.updateIncome(source.id, apiData);
            } catch (err) {
              console.error('[PersonEditPage] Failed to update income:', err);
            }
          }
        }

        // Clear deleted IDs after successful save
        setDeletedIncomeIds([]);
      }

      if (onSave) onSave(personData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  // Detail modal handlers
  const openDetailModal = (sourceIndex, year, incomeType) => {
    const source = incomeSources[sourceIndex];
    const yearData = source?.yearlyData?.[year] || {};

    // For entity-linked incomes, force REMUNERATION_DIRIGEANT type
    const effectiveIncomeType = source.isFromEntity ? 'REMUNERATION_DIRIGEANT' : incomeType;

    // Build initial data based on income type
    let initialData = {
      grossAnnual: yearData.grossAmount || 0,
      ...yearData.detailFields
    };

    // For REMUNERATION_DIRIGEANT, add entity-calculated values
    if (effectiveIncomeType === 'REMUNERATION_DIRIGEANT' || source.isFromEntity) {
      initialData = {
        ...initialData,
        grossAnnual: yearData.grossAmount || 0,
        chargesSalariales: yearData.charges || 0,
        netPercu: yearData.netAmount || 0,
        // Keep existing abatement settings if any
        abatementType: yearData.detailFields?.abatementType || 'percent10',
        fraisReels: yearData.detailFields?.fraisReels || 0
      };
    }

    setDetailModalData({
      sourceIndex,
      year,
      incomeType: effectiveIncomeType,
      initialData
    });
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => setDetailModalOpen(false);

  const saveDetailModalData = (data) => {
    const { sourceIndex, year } = detailModalData;
    if (sourceIndex === null || year === null) return;
    setIncomeSources(prev => prev.map((src, i) => {
      if (i !== sourceIndex) return src;
      return {
        ...src,
        yearlyData: { ...src.yearlyData, [year]: { ...src.yearlyData?.[year], charges: data.totalCharges || 0, detailFields: data } }
      };
    }));
  };

  if (loading) {
    return (
      <div className={`person-edit-page ${standalone ? 'standalone' : ''}`}>
        <div className="edit-loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className={`person-edit-page ${standalone ? 'standalone' : ''}`}>
      {/* Topbar - same style as wizard */}
      <div className="edit-topbar">
        <div className="topbar-left">
          <h3>
            {mode === 'template' ? 'Modifier template: ' : 'Modifier '}
            {personData.first_name} {personData.last_name}
          </h3>
          {standalone && (
            <span className="standalone-badge">Page complète</span>
          )}
        </div>
        <div className="topbar-right">
          <button
            className={`save-btn ${saving ? 'saving' : ''} ${saveSuccess ? 'success' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            Enregistrer
          </button>
          <button
            className="close-btn"
            onClick={onClose}
            title={standalone ? "Retour à l'accueil" : "Fermer"}
          >
            {standalone ? '← Retour' : '✕'}
          </button>
        </div>
      </div>

      {/* Content - scrollable body */}
      <div className="edit-body">
        {/* Row with 2 compact sections: Identity + Family */}
        <div className="person-header-row">
          <div className="form-section compact">
            <h4>Identité</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={personData.first_name}
                  onChange={e => handlePersonChange('first_name', e.target.value)}
                  placeholder="Prénom"
                />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={personData.last_name}
                  onChange={e => handlePersonChange('last_name', e.target.value)}
                  placeholder="Nom"
                />
              </div>
            </div>
          </div>

          <div className="form-section compact">
            <h4>Situation Familiale</h4>
            <div className="family-row">
              <div className="status-chips">
                {MARITAL_STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`status-chip ${personData.marital_status === opt.value ? 'selected' : ''}`}
                    onClick={() => handlePersonChange('marital_status', opt.value)}
                    title={opt.label}
                  >
                    <span className="chip-icon">{opt.icon}</span>
                  </button>
                ))}
              </div>
              <div className="children-inline">
                <span className="children-label">Enfants</span>
                <button type="button" className="child-btn" onClick={() => handleChildrenChange(-1)} disabled={personData.children_count === 0}>−</button>
                <span className="child-count">{personData.children_count}</span>
                <button type="button" className="child-btn" onClick={() => handleChildrenChange(1)}>+</button>
              </div>
              {showSingleParent && (
                <label className="single-parent-check">
                  <input
                    type="checkbox"
                    checked={personData.single_parent}
                    onChange={e => handlePersonChange('single_parent', e.target.checked)}
                  />
                  <span>Parent isolé</span>
                </label>
              )}
              <div className="fiscal-badge">
                <span className="fiscal-value">{fiscalParts}</span>
                <span className="fiscal-unit">part{fiscalParts > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Income sources section */}
        <div className="form-section incomes-section">
          <h4><span className="section-icon">1</span> Sources de revenus</h4>

          {/* Add income buttons by category - same as wizard */}
          <div className="income-add-section">
            {INCOME_CATEGORIES.map(category => (
              <div key={category.id} className="income-category-row">
                <span className="category-label">{category.label}</span>
                <div className="category-types">
                  {category.types.map(type => (
                    <button
                      key={type}
                      type="button"
                      className="add-type-btn"
                      onClick={() => addIncomeSource(type)}
                    >
                      <span className="btn-icon" style={{ background: INCOME_TYPE_CONFIG[type]?.color }}>
                        {INCOME_TYPE_CONFIG[type]?.icon}
                      </span>
                      <span className="btn-label">{INCOME_TYPE_CONFIG[type]?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Income sources list - all income types including dividends */}
          <div className="income-sources-list">
            {incomeSources.length === 0 ? (
              <div className="no-sources-hint">
                <p>Ajoutez des sources de revenus ci-dessus</p>
              </div>
            ) : (
              incomeSources.map((source, index) => {
                const config = INCOME_TYPE_CONFIG[source.type];
                const isExpanded = expandedSourceId === source.id;
                const years = getYearsForSource(source);
                const hasValidDates = sourceHasValidDates(source);
                const isDividend = source.type === 'DIVIDENDES' || source.isDividend;

                return (
                  <div key={source.id} className={`income-source-card ${isExpanded ? 'expanded' : ''} ${!hasValidDates ? 'needs-dates' : ''}`}>
                    <div className="source-header" onClick={() => hasValidDates && setExpandedSourceId(isExpanded ? null : source.id)}>
                      <span className="source-icon" style={{ background: config?.color }}>
                        {config?.icon}
                      </span>
                      <span className="source-type">{config?.label}</span>
                      <input
                        type="text"
                        className="source-label-input"
                        placeholder="Libellé (optionnel)"
                        value={source.label}
                        onChange={e => { e.stopPropagation(); updateIncomeSource(index, { label: e.target.value }); }}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="date-sep">Du</span>
                      <input
                        type="date"
                        className="source-date"
                        value={source.startDate}
                        onChange={e => { e.stopPropagation(); updateIncomeSource(index, { startDate: e.target.value }); }}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="date-sep">Au</span>
                      <input
                        type="date"
                        className="source-date"
                        value={source.endDate}
                        onChange={e => { e.stopPropagation(); updateIncomeSource(index, { endDate: e.target.value }); }}
                        onClick={e => e.stopPropagation()}
                      />
                      {hasValidDates && (
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                      )}
                      <button
                        type="button"
                        className="remove-source-btn"
                        onClick={e => { e.stopPropagation(); removeIncomeSource(index); }}
                      >×</button>
                    </div>

                    {isExpanded && hasValidDates && years.length > 0 && (
                      <div className="source-years-table">
                        {/* Show linked entity info for auto-generated incomes */}
                        {source.isAutoGenerated && source.linkedEntityName && !source.isDividend && (
                          <div className="linked-entity-banner">
                            <span className="link-icon">🔗</span>
                            <span>Brut/Net depuis <strong>{source.linkedEntityName}</strong></span>
                            <span className="editable-badge">⚙ Abattement modifiable</span>
                          </div>
                        )}
                        {source.isAutoGenerated && source.linkedEntityName && source.isDividend && (
                          <div className="linked-entity-banner dividend-banner">
                            <span className="link-icon">🔗</span>
                            <span>Dividendes depuis <strong>{source.linkedEntityName}</strong></span>
                            <span className="info-badge">Montants mis à jour automatiquement</span>
                          </div>
                        )}
                        <table>
                          <thead>
                            <tr>
                              <th
                                className="th-year readonly-col clickable-header"
                                onClick={() => isDividend && setHelpModal({ type: 'annee-ir', year: null, data: null })}
                              >
                                <span className="col-label">{isDividend ? 'Année IR' : 'Année'}</span>
                                {isDividend && <span className="col-help-icon">ⓘ</span>}
                              </th>
                              <th
                                className={`th-brut ${source.isFromEntity ? 'readonly-col' : 'editable-col'} clickable-header`}
                                onClick={() => isDividend && setHelpModal({ type: 'brut', year: null, data: { isFromEntity: source.isFromEntity } })}
                              >
                                <span className="col-label">{isDividend ? 'Brut' : (config?.brutLabel || 'Brut annuel')}</span>
                                {isDividend && <span className="col-help-icon">ⓘ</span>}
                              </th>
                              <th className="th-detail"></th>
                              {isDividend ? (
                                <>
                                  <th
                                    className="th-regime editable-col clickable-header"
                                    onClick={() => setHelpModal({ type: 'regime', year: null, data: null })}
                                  >
                                    <span className="col-label">Régime</span>
                                    <span className="col-help-icon">ⓘ</span>
                                  </th>
                                  <th
                                    className="th-dispense editable-col clickable-header"
                                    onClick={() => setHelpModal({ type: 'dispense', year: null, data: null })}
                                  >
                                    <span className="col-label">Dispense</span>
                                    <span className="col-help-icon">ⓘ</span>
                                  </th>
                                  <th
                                    className="th-acompte readonly-col clickable-header"
                                    onClick={() => setHelpModal({ type: 'acompte', year: null, data: null })}
                                  >
                                    <span className="col-label">Acompte</span>
                                    <span className="col-help-icon">ⓘ</span>
                                  </th>
                                  <th
                                    className="th-regul readonly-col clickable-header"
                                    onClick={() => setHelpModal({ type: 'regul', year: null, data: null })}
                                  >
                                    <span className="col-label">Régul IR</span>
                                    <span className="col-help-icon">ⓘ</span>
                                  </th>
                                  <th
                                    className="th-net readonly-col clickable-header"
                                    onClick={() => setHelpModal({ type: 'net-final', year: null, data: null })}
                                  >
                                    <span className="col-label">Net / Imp.</span>
                                    <span className="col-help-icon">ⓘ</span>
                                  </th>
                                </>
                              ) : (
                                <>
                                  <th className="th-charges">Charges sal.</th>
                                  <th className="th-net">Net perçu</th>
                                  <th className="th-taxable">Imposable</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {years.map(year => {
                              const yearData = source.yearlyData?.[year] || {};
                              const isFromEntity = source.isFromEntity || yearData.isFromEntity;

                              // Get brut
                              const brut = yearData.grossAmount || yearData.netAmount || 0;

                              if (isDividend) {
                                // Dividend-specific calculations
                                const totals = calculateYearTotals(parseInt(year));
                                const { tmi } = calculateIRForYear(parseInt(year), totals.totalTaxable, fiscalParts);
                                const yearTaxOption = getTaxOptionForYear(parseInt(year));
                                const dispenseData = yearData.dispense || { checked: false, date: '' };
                                const taxCalc = calculateDividendTax(parseInt(year), brut, yearTaxOption, tmi);

                                // Adjust acompte if dispense is active (only IR portion is dispensed)
                                const effectiveAcompteIR = dispenseData.checked ? 0 : taxCalc.acompteIR;
                                const effectiveTotalAcompte = effectiveAcompteIR + taxCalc.acomptePS;

                                return (
                                  <tr key={year} className={isFromEntity ? 'from-entity' : ''}>
                                    <td className="td-year">{year}</td>
                                    <td className="td-brut">
                                      {isFromEntity ? (
                                        <span className="readonly-value">{brut.toLocaleString('fr-FR')} €</span>
                                      ) : (
                                        <div className={`input-wrapper ${brut === 0 ? 'needs-input' : ''}`}>
                                          <input
                                            type="number"
                                            value={yearData.grossAmount || ''}
                                            onChange={e => updateYearlyData(index, year, { grossAmount: parseFloat(e.target.value) || 0 })}
                                            placeholder="0"
                                          />
                                          <span className="input-suffix">€</span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="td-detail">
                                      <button
                                        type="button"
                                        className="detail-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setDividendPopover({ visible: true, year: parseInt(year), dividendInfo: { ...taxCalc, dispenseActive: dispenseData.checked } });
                                        }}
                                        title="Voir le détail du calcul"
                                      >
                                        ?
                                      </button>
                                    </td>
                                    <td className="td-regime">
                                      <select
                                        value={yearTaxOption}
                                        onChange={(e) => updateTaxOptionForYear(parseInt(year), e.target.value)}
                                        className="regime-select"
                                        title="Ce choix s'applique à TOUS vos revenus du capital de cette année"
                                      >
                                        <option value="PFU">PFU</option>
                                        <option value="BAREME">Barème</option>
                                      </select>
                                    </td>
                                    <td className="td-dispense">
                                      <div className="dispense-control">
                                        <input
                                          type="checkbox"
                                          checked={dispenseData.checked}
                                          onChange={(e) => updateDispenseForYear(index, year, {
                                            ...dispenseData,
                                            checked: e.target.checked
                                          })}
                                          title="Dispense d'acompte IR (si éligible)"
                                        />
                                        {dispenseData.checked && (
                                          <input
                                            type="date"
                                            value={dispenseData.date || ''}
                                            onChange={(e) => updateDispenseForYear(index, year, {
                                              ...dispenseData,
                                              date: e.target.value
                                            })}
                                            className="dispense-date"
                                            title="Date de la demande (avant le 30/11 N-1)"
                                            placeholder="Date demande"
                                          />
                                        )}
                                      </div>
                                    </td>
                                    <td className="td-acompte">
                                      <span className={`auto-value ${effectiveTotalAcompte > 0 ? 'neg' : ''}`}>
                                        {effectiveTotalAcompte > 0 ? '-' : ''}{effectiveTotalAcompte.toLocaleString('fr-FR')} €
                                        {dispenseData.checked && <span className="dispense-badge" title="Acompte IR dispensé">✓</span>}
                                      </span>
                                    </td>
                                    <td className="td-regul">
                                      <span className={`auto-value ${taxCalc.regulIR > 0 ? 'neg' : taxCalc.regulIR < 0 ? 'pos' : ''}`}>
                                        {taxCalc.regulIR === 0 ? '0 €' : (taxCalc.regulIR > 0 ? '+' : '') + taxCalc.regulIR.toLocaleString('fr-FR') + ' €'}
                                      </span>
                                    </td>
                                    <td className="td-net">
                                      {yearTaxOption === 'PFU' ? (
                                        <span className="auto-value highlight" title="Net définitif après flat tax 30%">
                                          {taxCalc.netApresImpot.toLocaleString('fr-FR')} €
                                        </span>
                                      ) : (
                                        <span className="auto-value imposable-value" title="Base imposable après abattement 40% - Le net réel dépend du TMI calculé sur TOUS vos revenus">
                                          {taxCalc.details.baseImposable?.toLocaleString('fr-FR')} €
                                          <span className="imposable-badge">IMP</span>
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              }

                              // Regular income calculations
                              const charges = yearData.charges || Math.round(brut * (config?.chargesRate || 0));
                              const netPercu = yearData.netAmount !== undefined ? yearData.netAmount : (brut - charges);
                              const abatementType = yearData.detailFields?.abatementType || 'percent10';
                              let abatement = 0;
                              if (netPercu > 0) {
                                if (abatementType === 'percent10') {
                                  abatement = Math.min(Math.max(Math.round(netPercu * 0.10), 495), 14171);
                                } else if (abatementType === 'fraisReels') {
                                  abatement = yearData.detailFields?.fraisReels || 0;
                                }
                              }
                              const taxable = Math.max(0, netPercu - abatement);
                              const hasDetail = !!yearData.detailFields;

                              return (
                                <tr
                                  key={year}
                                  className={isFromEntity ? 'from-entity' : ''}
                                  onClick={isFromEntity ? (e) => e.stopPropagation() : undefined}
                                >
                                  <td className="td-year">{year}</td>
                                  <td className="td-brut">
                                    {isFromEntity ? (
                                      <div
                                        className="readonly-value clickable with-detail"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setCalcPopover({
                                            visible: true,
                                            sourceIndex: index,
                                            year
                                          });
                                        }}
                                        title="Cliquer pour voir le détail du calcul"
                                      >
                                        <span>{brut.toLocaleString('fr-FR')} €</span>
                                        {yearData.prorata && yearData.prorata < 100 && (
                                          <span className="prorata-info">
                                            ({yearData.prorata}%)
                                          </span>
                                        )}
                                        <span className="calc-info-icon small">?</span>
                                      </div>
                                    ) : (
                                      <div className={`input-wrapper ${brut === 0 ? 'needs-input' : ''}`}>
                                        <input
                                          type="number"
                                          value={yearData.grossAmount || ''}
                                          onChange={e => updateYearlyData(index, year, { grossAmount: parseFloat(e.target.value) || 0 })}
                                          placeholder="0"
                                        />
                                        <span className="input-suffix">€</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="td-detail">
                                    <button
                                      type="button"
                                      className={`detail-btn ${hasDetail ? 'has-detail' : ''}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openDetailModal(index, year, source.type);
                                      }}
                                      title={isFromEntity
                                        ? 'Cliquer pour choisir le mode de déclaration : abattement forfaitaire 10% ou frais réels'
                                        : 'Cliquer pour détailler les charges et configurer l\'abattement'}
                                    >
                                      ⚙
                                    </button>
                                  </td>
                                  <td className="td-charges">
                                    <span className="auto-value neg">{charges > 0 ? `-${charges.toLocaleString('fr-FR')} €` : '—'}</span>
                                  </td>
                                  <td className="td-net">
                                    <span className="auto-value">{netPercu > 0 ? `${netPercu.toLocaleString('fr-FR')} €` : '—'}</span>
                                  </td>
                                  <td className="td-taxable">
                                    <span
                                      className="auto-value"
                                      title={`Net ${netPercu.toLocaleString('fr-FR')}€ - Abattement ${abatement.toLocaleString('fr-FR')}€ (${abatementType === 'percent10' ? '10%' : 'frais réels'})`}
                                    >
                                      {taxable > 0 ? `${taxable.toLocaleString('fr-FR')} €` : '—'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!hasValidDates && (
                      <div className="dates-hint">
                        ← Sélectionnez les dates de début et fin
                      </div>
                    )}
                  </div>
                );
              })
            )}

          </div>
        </div>

        {/* Section 3: DansMaPoche summary - same as wizard step 3 */}
        <div className="form-section summary-section">
          <h4><span className="section-icon">2</span> DansMaPoche (prévisionnel)</h4>

          {coveredYears.length > 0 ? (
            <div className="dansmapoche-years">
              {coveredYears.map(year => {
                const calc = calculateDansMaPocheForYear(year);
                const hasDividends = calc.dividend.brut > 0;

                return (
                  <div
                    key={year}
                    className="dansmapoche-year-card clickable"
                    onClick={() => setDmpBreakdown({ visible: true, data: calc })}
                    title="Cliquer pour voir le détail du calcul"
                  >
                    <div className="year-label">
                      {year}
                      <span className="view-detail-icon">🔍</span>
                    </div>
                    <div className="year-breakdown">
                      {/* Regular income */}
                      {calc.regular.net > 0 && (
                        <div className="breakdown-row net-row">
                          <span className="row-label">Revenus nets</span>
                          <span className="row-value">{formatCurrency(calc.regular.net)}</span>
                        </div>
                      )}
                      {calc.regular.net > 0 && (
                        <div className="breakdown-row ir-row">
                          <span className="row-label">− IR ({calc.tmi}%)</span>
                          <span className="row-value negative">{formatCurrency(calc.regular.ir)}</span>
                        </div>
                      )}

                      {/* Dividend income */}
                      {hasDividends && (
                        <>
                          <div className="breakdown-row dividend-row">
                            <span className="row-label">
                              Dividendes bruts
                              <span className={`tax-badge ${calc.taxOption === 'PFU' ? 'pfu' : 'bareme'}`}>
                                {calc.taxOption}
                              </span>
                            </span>
                            <span className="row-value">{formatCurrency(calc.dividend.brut)}</span>
                          </div>
                          <div className="breakdown-row ir-row">
                            <span className="row-label">− Impôts div. (IR+PS)</span>
                            <span className="row-value negative">{formatCurrency(calc.dividend.totalTax)}</span>
                          </div>
                        </>
                      )}

                      {/* Final DansMaPoche */}
                      <div className="breakdown-row dansmapoche-row">
                        <span className="row-label">DansMaPoche</span>
                        <span className="row-value highlight">{formatCurrency(calc.totals.dansMaPoche)}</span>
                      </div>
                    </div>
                    <div className="monthly-equivalent">
                      soit {formatCurrency(Math.round(calc.totals.dansMaPoche / 12))}/mois
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-data-message">
              <p>Ajoutez des revenus pour voir le calcul DansMaPoche</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <IncomeDetailModal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        incomeType={detailModalData.incomeType}
        year={detailModalData.year}
        initialData={detailModalData.initialData}
        onSave={saveDetailModalData}
      />

      {/* Calculation Breakdown Popover */}
      {calcPopover.visible && (() => {
        const source = incomeSources[calcPopover.sourceIndex];
        const yearData = source?.yearlyData?.[calcPopover.year];
        const details = yearData?.calculationDetails || [];
        const civilYear = calcPopover.year;
        const prorata = yearData?.prorata || 100;

        return (
          <div
            className="calc-popover-overlay"
            onClick={() => setCalcPopover({ ...calcPopover, visible: false })}
          >
            <div
              className="calc-popover"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="calc-popover-header">
                <span>📊 Calcul année civile {civilYear}</span>
                <button onClick={() => setCalcPopover({ ...calcPopover, visible: false })}>×</button>
              </div>
              <div className="calc-popover-body">
                {details.length > 0 ? (
                  <>
                    <div className="calc-explanation">
                      <div className="explanation-title">🎯 Objectif</div>
                      <p>
                        Calculer le revenu pour l'<strong>année civile {civilYear}</strong> (1er janv. → 31 déc.)
                        à partir des exercices fiscaux de l'entreprise.
                      </p>
                    </div>

                    <div className="calc-explanation">
                      <div className="explanation-title">📅 Couverture</div>
                      <p>
                        {prorata}% de l'année civile est couverte par des revenus de l'entité.
                      </p>
                    </div>

                    <div className="calc-explanation">
                      <div className="explanation-title">🧮 Répartition par exercice fiscal</div>
                    </div>

                    {details.map((d, i) => (
                      <div key={i} className="calc-exercice">
                        <div className="exercice-header">
                          <span>Exercice fiscal {d.exerciceKey}</span>
                          <span className="exercice-days">{d.overlapDays}j sur {d.exerciceTotalDays}j</span>
                        </div>
                        <div className="calc-detail-row">
                          <div className="detail-line">
                            <span className="detail-label">Brut exercice :</span>
                            <span className="detail-value">{d.exerciceBrut.toLocaleString('fr-FR')} €</span>
                          </div>
                          <div className="detail-line formula">
                            <span>→ Part année {civilYear} :</span>
                            <span>{d.exerciceBrut.toLocaleString('fr-FR')} × {d.overlapDays}/{d.exerciceTotalDays} = <strong>{d.brutForPeriod.toLocaleString('fr-FR')} €</strong></span>
                          </div>
                        </div>
                        <div className="calc-detail-row">
                          <div className="detail-line">
                            <span className="detail-label">Net exercice :</span>
                            <span className="detail-value">{d.exerciceNet.toLocaleString('fr-FR')} €</span>
                          </div>
                          <div className="detail-line formula">
                            <span>→ Part année {civilYear} :</span>
                            <span>{d.exerciceNet.toLocaleString('fr-FR')} × {d.overlapDays}/{d.exerciceTotalDays} = <strong>{d.netForPeriod.toLocaleString('fr-FR')} €</strong></span>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="calc-total">
                      <div className="total-header">📈 Total année civile {civilYear}</div>
                      <div className="total-row with-formula">
                        <span className="total-label">Brut annuel :</span>
                        <span className="total-formula">
                          {details.map(d => d.brutForPeriod.toLocaleString('fr-FR')).join(' + ')} =
                        </span>
                        <span className="total-value">{(yearData.grossAmount || 0).toLocaleString('fr-FR')} €</span>
                      </div>
                      <div className="total-row with-formula">
                        <span className="total-label">Net perçu :</span>
                        <span className="total-formula">
                          {details.map(d => d.netForPeriod.toLocaleString('fr-FR')).join(' + ')} =
                        </span>
                        <span className="total-value">{(yearData.netAmount || 0).toLocaleString('fr-FR')} €</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="calc-empty">Pas de détails disponibles</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dividend Calculation Popover */}
      {dividendPopover.visible && dividendPopover.dividendInfo && (() => {
        const info = dividendPopover.dividendInfo;
        const year = dividendPopover.year;

        return (
          <div
            className="calc-popover-overlay"
            onClick={() => setDividendPopover({ visible: false, year: null, dividendInfo: null })}
          >
            <div
              className="calc-popover dividend-popover"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="calc-popover-header dividend-header">
                <span>💰 Calcul imposition dividendes {year}</span>
                <button onClick={() => setDividendPopover({ visible: false, year: null, dividendInfo: null })}>×</button>
              </div>
              <div className="calc-popover-body">
                {/* Tax option badge */}
                <div className="dividend-tax-badge-large">
                  {info.taxOption === 'PFU' ? (
                    <>
                      <span className="badge-icon">📋</span>
                      <span className="badge-text">Prélèvement Forfaitaire Unique (Flat Tax)</span>
                    </>
                  ) : (
                    <>
                      <span className="badge-icon">📊</span>
                      <span className="badge-text">Barème progressif (case 2OP cochée)</span>
                    </>
                  )}
                </div>

                {/* Timeline */}
                <div className="dividend-timeline">
                  <div className="timeline-title">📅 Chronologie des flux</div>

                  {/* T0 - Payment */}
                  <div className="timeline-step">
                    <div className="step-marker">T₀</div>
                    <div className="step-content">
                      <div className="step-title">Versement des dividendes</div>
                      <div className="step-detail">
                        <span>Montant brut perçu :</span>
                        <span className="step-value">{info.brut.toLocaleString('fr-FR')} €</span>
                      </div>
                      <div className="step-detail highlight-neg">
                        <span>Acompte prélevé par l'entreprise :</span>
                        <span className="step-value">-{info.totalAcompte.toLocaleString('fr-FR')} €</span>
                      </div>
                      <div className="step-breakdown">
                        <span>• IR (12,8%) : {info.acompteIR.toLocaleString('fr-FR')} €</span>
                        <span>• PS (17,2%) : {info.acomptePS.toLocaleString('fr-FR')} €</span>
                      </div>
                      <div className="step-result">
                        <span>Net versé sur compte :</span>
                        <span className="step-value">{(info.brut - info.totalAcompte).toLocaleString('fr-FR')} €</span>
                      </div>
                    </div>
                  </div>

                  {/* T1 - Declaration */}
                  <div className="timeline-step">
                    <div className="step-marker">T₁</div>
                    <div className="step-content">
                      <div className="step-title">Déclaration IR (année suivante)</div>
                      {info.taxOption === 'PFU' ? (
                        <div className="step-detail info">
                          <span>PFU : imposition libératoire, pas de régularisation</span>
                        </div>
                      ) : (
                        <>
                          <div className="step-detail">
                            <span>Base imposable (après abattement 40%) :</span>
                            <span className="step-value">{info.details.baseImposable?.toLocaleString('fr-FR')} €</span>
                          </div>
                          <div className="step-formula">
                            {info.brut.toLocaleString('fr-FR')} € × 60% = {info.details.baseImposable?.toLocaleString('fr-FR')} €
                          </div>
                          <div className="step-detail">
                            <span>IR au barème (TMI {info.details.tmi}%) :</span>
                            <span className="step-value">{info.finalIR.toLocaleString('fr-FR')} €</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* T2 - Regularization */}
                  <div className="timeline-step">
                    <div className="step-marker">T₂</div>
                    <div className="step-content">
                      <div className="step-title">Régularisation</div>
                      {info.taxOption === 'PFU' ? (
                        <div className="step-detail neutral">
                          <span>Aucune régularisation (PFU libératoire)</span>
                          <span className="step-value">0 €</span>
                        </div>
                      ) : (
                        <div className={`step-detail ${info.regulIR > 0 ? 'highlight-neg' : info.regulIR < 0 ? 'highlight-pos' : 'neutral'}`}>
                          <span>{info.regulIR > 0 ? 'Complément à payer :' : info.regulIR < 0 ? 'Remboursement :' : 'Aucune régularisation :'}</span>
                          <span className="step-value">
                            {info.regulIR !== 0 ? (info.regulIR > 0 ? '+' : '') + info.regulIR.toLocaleString('fr-FR') + ' €' : '0 €'}
                          </span>
                        </div>
                      )}
                      {info.taxOption === 'BAREME' && (
                        <div className="step-formula">
                          IR final ({info.finalIR.toLocaleString('fr-FR')} €) - Acompte IR ({info.acompteIR.toLocaleString('fr-FR')} €) = {info.regulIR.toLocaleString('fr-FR')} €
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Final summary */}
                <div className="dividend-summary">
                  <div className="summary-title">📈 Bilan final</div>
                  <div className="summary-row">
                    <span>Dividendes bruts perçus</span>
                    <span>{info.brut.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="summary-row neg">
                    <span>Total IR payé</span>
                    <span>-{info.finalIR.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="summary-row neg">
                    <span>Total PS payés</span>
                    <span>-{info.finalPS.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="summary-row total">
                    <span>Net après impôts</span>
                    <span>{info.netApresImpot.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="summary-rate">
                    Taux effectif d'imposition : {Math.round((1 - info.netApresImpot / info.brut) * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DansMaPoche Breakdown Modal */}
      {dmpBreakdown.visible && dmpBreakdown.data && (() => {
        const d = dmpBreakdown.data;
        const hasDividends = d.dividend.brut > 0;
        const hasRegular = d.regular.net > 0;

        return (
          <div
            className="calc-popover-overlay"
            onClick={() => setDmpBreakdown({ visible: false, data: null })}
          >
            <div
              className="calc-popover dmp-breakdown-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="calc-popover-header dmp-header">
                <span>📊 DansMaPoche {d.year} - Détail du calcul</span>
                <button onClick={() => setDmpBreakdown({ visible: false, data: null })}>×</button>
              </div>
              <div className="calc-popover-body">
                {/* Fiscal situation */}
                <div className="dmp-section">
                  <div className="dmp-section-title">👤 Situation fiscale</div>
                  <div className="dmp-detail-row">
                    <span>Parts fiscales</span>
                    <span>{d.fiscalParts}</span>
                  </div>
                  <div className="dmp-detail-row">
                    <span>TMI (Taux Marginal)</span>
                    <span>{d.tmi}%</span>
                  </div>
                </div>

                {/* Income sources breakdown */}
                {d.incomeDetails.length > 0 && (
                  <div className="dmp-section">
                    <div className="dmp-section-title">📋 Détail des revenus</div>
                    {d.incomeDetails.map((inc, i) => (
                      <div key={i} className={`dmp-income-item ${inc.type}`}>
                        <div className="income-label">
                          {inc.type === 'dividend' ? '💰' : '💼'} {inc.label}
                          {inc.linkedEntity && <span className="linked-tag">🔗 {inc.linkedEntity}</span>}
                        </div>
                        {inc.type === 'dividend' ? (
                          <div className="income-values">
                            <span>Brut: {inc.brut.toLocaleString('fr-FR')} €</span>
                          </div>
                        ) : (
                          <div className="income-values">
                            <span>Brut: {inc.brut?.toLocaleString('fr-FR')} €</span>
                            {inc.charges > 0 && <span>Charges: -{inc.charges.toLocaleString('fr-FR')} €</span>}
                            <span>Net: {inc.net?.toLocaleString('fr-FR')} €</span>
                            <span>Imposable: {inc.taxable?.toLocaleString('fr-FR')} €</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Regular income calculation */}
                {hasRegular && (
                  <div className="dmp-section">
                    <div className="dmp-section-title">💼 Revenus réguliers</div>
                    <div className="dmp-calc-row">
                      <span>Net perçu</span>
                      <span className="positive">{formatCurrency(d.regular.net)}</span>
                    </div>
                    <div className="dmp-calc-row">
                      <span>Base imposable</span>
                      <span>{formatCurrency(d.regular.taxable)}</span>
                    </div>
                    <div className="dmp-calc-row highlight-neg">
                      <span>IR barème ({d.tmi}%)</span>
                      <span>-{formatCurrency(d.regular.ir)}</span>
                    </div>
                    <div className="dmp-calc-row result">
                      <span>Net après IR</span>
                      <span>{formatCurrency(d.regular.net - d.regular.ir)}</span>
                    </div>
                  </div>
                )}

                {/* Dividend calculation */}
                {hasDividends && (
                  <div className="dmp-section">
                    <div className="dmp-section-title">
                      💰 Dividendes
                      <span className={`tax-badge-large ${d.taxOption === 'PFU' ? 'pfu' : 'bareme'}`}>
                        {d.taxOption === 'PFU' ? 'PFU (Flat Tax 30%)' : 'Barème progressif'}
                      </span>
                    </div>
                    <div className="dmp-calc-row">
                      <span>Dividendes bruts</span>
                      <span className="positive">{formatCurrency(d.dividend.brut)}</span>
                    </div>

                    {d.taxOption === 'PFU' ? (
                      <>
                        <div className="dmp-calc-row highlight-neg">
                          <span>IR forfaitaire (12,8%)</span>
                          <span>-{formatCurrency(d.dividend.ir)}</span>
                        </div>
                        <div className="dmp-calc-row highlight-neg">
                          <span>Prélèvements sociaux (17,2%)</span>
                          <span>-{formatCurrency(d.dividend.ps)}</span>
                        </div>
                        <div className="dmp-formula">
                          {d.dividend.brut.toLocaleString('fr-FR')} × 30% = {d.dividend.totalTax.toLocaleString('fr-FR')} €
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="dmp-calc-row">
                          <span>Abattement 40%</span>
                          <span>-{formatCurrency(Math.round(d.dividend.brut * 0.40))}</span>
                        </div>
                        <div className="dmp-calc-row">
                          <span>Base imposable (60%)</span>
                          <span>{formatCurrency(d.dividend.taxable)}</span>
                        </div>
                        <div className="dmp-calc-row highlight-neg">
                          <span>IR barème (TMI {d.tmi}%)</span>
                          <span>-{formatCurrency(d.dividend.ir)}</span>
                        </div>
                        <div className="dmp-calc-row highlight-neg">
                          <span>Prélèvements sociaux (17,2%)</span>
                          <span>-{formatCurrency(d.dividend.ps)}</span>
                        </div>
                        <div className="dmp-formula">
                          IR: {d.dividend.taxable.toLocaleString('fr-FR')} × {d.tmi}% = {d.dividend.ir.toLocaleString('fr-FR')} €
                        </div>
                      </>
                    )}

                    <div className="dmp-calc-row result">
                      <span>Net dividendes</span>
                      <span>{formatCurrency(d.dividend.net)}</span>
                    </div>
                  </div>
                )}

                {/* Final summary */}
                <div className="dmp-section dmp-summary">
                  <div className="dmp-section-title">📈 Récapitulatif {d.year}</div>
                  <div className="dmp-calc-row">
                    <span>Total revenus nets perçus</span>
                    <span className="positive">{formatCurrency(d.totals.net)}</span>
                  </div>
                  <div className="dmp-calc-row highlight-neg">
                    <span>Total impôts et prélèvements</span>
                    <span>-{formatCurrency(d.totals.tax)}</span>
                  </div>
                  <div className="dmp-calc-row final">
                    <span>🎯 DansMaPoche</span>
                    <span className="highlight">{formatCurrency(d.totals.dansMaPoche)}</span>
                  </div>
                  <div className="dmp-monthly">
                    soit {formatCurrency(Math.round(d.totals.dansMaPoche / 12))} / mois
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Help Modal: Année IR */}
      {helpModal.type === 'annee-ir' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal help-modal-small" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Année IR (Année d'imposition)</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <p>
                  L'<strong>Année IR</strong> est l'année où vous déclarerez ces dividendes à l'administration fiscale.
                </p>
                <p>
                  Elle correspond à <strong>l'année de versement</strong> des dividendes, pas à l'exercice de la société.
                </p>
              </div>
              <div className="help-section">
                <h4>📅 Exemple</h4>
                <p className="help-note">
                  Exercice 2024 (clôture 31/12/2024) → AG en juin 2025 → Versement juillet 2025 → <strong>Année IR 2025</strong> (déclaré en 2026)
                </p>
              </div>
              <div className="help-section">
                <p><span className="col-lock-icon">🔒</span> Cette valeur est calculée automatiquement à partir de la date de versement définie dans la société.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Brut */}
      {helpModal.type === 'brut' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal help-modal-small" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Dividendes Bruts</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <p>
                  Le montant <strong>brut</strong> des dividendes que vous recevez de la société, avant tout prélèvement fiscal ou social.
                </p>
              </div>
              {helpModal.data?.isFromEntity ? (
                <div className="help-section highlight-pfu">
                  <h4>🔗 Lié à la société</h4>
                  <p>Ce montant est calculé automatiquement depuis les données de la société :</p>
                  <ul>
                    <li>Résultat net de l'exercice</li>
                    <li>× Pourcentage de distribution (affectation)</li>
                    <li>× Votre pourcentage de détention</li>
                  </ul>
                  <p className="help-note">Pour modifier ce montant, ajustez les paramètres dans la fiche de la société.</p>
                </div>
              ) : (
                <div className="help-section highlight-bareme">
                  <h4>✏️ Saisie manuelle</h4>
                  <p>Vous pouvez saisir librement le montant brut des dividendes reçus d'une source externe.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Régime */}
      {helpModal.type === 'regime' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Régime fiscal des dividendes</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <p>Vous avez le choix entre deux régimes pour l'imposition de vos dividendes :</p>
              </div>

              <div className="help-section highlight-pfu">
                <h4>🔵 PFU (Prélèvement Forfaitaire Unique) - "Flat Tax"</h4>
                <p><strong>Taux fixe de 30%</strong> composé de :</p>
                <ul>
                  <li>12,8% d'impôt sur le revenu</li>
                  <li>17,2% de prélèvements sociaux</li>
                </ul>
                <p className="help-note">
                  ✅ Simple et prévisible<br/>
                  ✅ Avantageux si TMI ≥ 30%<br/>
                  ❌ Pas d'abattement de 40%
                </p>
              </div>

              <div className="help-section highlight-bareme">
                <h4>🟠 Barème progressif de l'IR</h4>
                <p>Les dividendes sont ajoutés à vos autres revenus après un <strong>abattement de 40%</strong>.</p>
                <ul>
                  <li>Base imposable = Dividendes × 60%</li>
                  <li>+ 17,2% de prélèvements sociaux (sur 100%)</li>
                </ul>
                <p className="help-note">
                  ✅ Avantageux si TMI ≤ 11%<br/>
                  ✅ Abattement de 40%<br/>
                  ❌ Dépend de tous vos revenus
                </p>
              </div>

              <div className="help-section">
                <h4>⚠️ Choix global pour l'année</h4>
                <p>
                  <strong>Ce choix s'applique à TOUS vos revenus du capital de l'année</strong> (dividendes de toutes sociétés, intérêts, plus-values...).
                </p>
                <p>Vous ne pouvez pas choisir PFU pour une société et Barème pour une autre.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Acompte */}
      {helpModal.type === 'acompte' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Acompte sur dividendes</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <h4>📋 Qu'est-ce que l'acompte ?</h4>
                <p>
                  Au moment du versement des dividendes, la société <strong>prélève automatiquement</strong> un acompte sur les impôts et charges dus :
                </p>
                <ul>
                  <li><strong>17,2%</strong> de prélèvements sociaux (PS) - toujours prélevés</li>
                  <li><strong>12,8%</strong> d'impôt sur le revenu (IR) - sauf si dispense</li>
                </ul>
                <p>Soit un total de <strong>30%</strong> prélevé à la source.</p>
              </div>

              <div className="help-section">
                <h4>💰 Impact sur la trésorerie</h4>
                <p>La société verse cet acompte à l'État pour votre compte :</p>
                <ul>
                  <li>Vous recevez : Brut - Acompte = <strong>70%</strong> du brut</li>
                  <li>L'État reçoit immédiatement les 30%</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>🔄 Et après ?</h4>
                <p>Lors de votre déclaration de revenus :</p>
                <ul>
                  <li><strong>PFU</strong> : L'acompte IR (12,8%) est définitif → rien à régulariser</li>
                  <li><strong>Barème</strong> : L'acompte IR est comparé à l'impôt réel → crédit ou complément</li>
                </ul>
                <p className="help-note">Les PS (17,2%) sont toujours définitifs quel que soit le régime.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Net Final / Imposable */}
      {helpModal.type === 'net-final' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Net final / Imposable</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <h4>📋 Cette colonne affiche une valeur différente selon le régime</h4>
                <p>Le contenu dépend du choix PFU ou Barème que vous avez fait :</p>
              </div>

              <div className="help-section highlight-pfu">
                <h4>🔵 En PFU → Affiche le "Net final"</h4>
                <p><strong>Net final = Brut × 70%</strong></p>
                <p>C'est le montant définitif qui vous reste après la flat tax de 30%.</p>
                <p className="help-note">
                  Simple et prévisible : vous gardez toujours exactement 70% du brut, quels que soient vos autres revenus.
                </p>
              </div>

              <div className="help-section highlight-bareme">
                <h4>🟠 En Barème → Affiche l'"Imposable" <span className="imposable-badge-inline">IMP</span></h4>
                <p><strong>Imposable = Brut × 60%</strong> (après abattement de 40%)</p>
                <p>
                  Ce montant sera <strong>ajouté à vos autres revenus</strong> pour calculer votre IR global au barème progressif.
                </p>
                <p className="help-note">
                  En barème, on ne peut pas afficher de "Net final" car il dépend de TOUS vos revenus (salaires, foncier, etc.) qui déterminent votre TMI.
                </p>
              </div>

              <div className="help-section">
                <h4>💡 Pourquoi cette différence ?</h4>
                <ul>
                  <li><strong>PFU</strong> : imposition séparée à 30%, indépendante des autres revenus</li>
                  <li><strong>Barème</strong> : dividendes intégrés au revenu global, TMI variable selon situation</li>
                </ul>
                <p>
                  En barème, le net réel sera calculé lors de votre déclaration de revenus, en fonction de l'ensemble de vos revenus de l'année.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Dispense */}
      {helpModal.type === 'dispense' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Dispense d'acompte IR sur dividendes</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <h4>📋 Qu'est-ce que la dispense ?</h4>
                <p>
                  Normalement, quand une société verse des dividendes, elle prélève automatiquement un <strong>acompte de 12,8%</strong> au titre de l'impôt sur le revenu (en plus des 17,2% de prélèvements sociaux).
                </p>
                <p>
                  La <strong>dispense</strong> permet d'éviter ce prélèvement de 12,8% au moment du versement. Vous paierez l'intégralité de l'IR lors de votre déclaration de revenus.
                </p>
              </div>

              <div className="help-section highlight-warning">
                <h4>⚠️ Vérifiez votre éligibilité</h4>
                <p>Vous pouvez demander la dispense <strong>uniquement si</strong> votre <strong>Revenu Fiscal de Référence (RFR) de l'année N-2</strong> est inférieur à :</p>
                <ul>
                  <li><strong>50 000 €</strong> pour une personne seule</li>
                  <li><strong>75 000 €</strong> pour un couple (imposition commune)</li>
                </ul>
                <p className="help-note">
                  Exemple : Pour des dividendes versés en 2025, votre RFR 2023 (figurant sur votre avis d'imposition 2024) doit être &lt; 50k€ ou 75k€.
                </p>
                <p className="help-note warning">
                  <strong>Important :</strong> Consultez votre dernier avis d'imposition pour connaître votre RFR avant de cocher cette case.
                </p>
              </div>

              <div className="help-section">
                <h4>📅 La date de demande</h4>
                <p>
                  La demande de dispense doit être faite <strong>avant le 30 novembre de l'année précédant le versement</strong>.
                </p>
                <p className="help-note">
                  Exemple : Pour des dividendes versés en juillet 2025, la demande doit être faite avant le 30/11/2024.
                </p>
                <p>
                  En cochant la case et en indiquant une date, vous déclarez avoir fait cette demande auprès de votre société.
                </p>
              </div>

              <div className="help-section">
                <h4>💡 Avantage de la dispense</h4>
                <ul>
                  <li>Vous conservez votre trésorerie plus longtemps (12,8% non prélevé immédiatement)</li>
                  <li>Utile si votre TMI est faible et que vous optez pour le barème progressif</li>
                  <li>L'impôt final reste le même - seul le moment du paiement change</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>🏢 Impact sur la société</h4>
                <p>
                  La dispense <strong>n'affecte pas la trésorerie de la société</strong>. Le montant total décaissé par la société reste identique (dividendes bruts).
                </p>
                <p>
                  Seule la répartition change : avec dispense, plus d'argent va directement sur votre compte, moins vers l'État au moment du versement.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal: Régul IR */}
      {helpModal.type === 'regul' && (
        <div className="help-modal-overlay" onClick={() => setHelpModal({ type: null, year: null, data: null })}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <span>ℹ️ Régularisation IR sur dividendes</span>
              <button onClick={() => setHelpModal({ type: null, year: null, data: null })}>×</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <h4>📋 Qu'est-ce que la Régul IR ?</h4>
                <p>
                  La <strong>Régul IR</strong> est l'ajustement effectué lors de votre déclaration de revenus entre l'acompte déjà prélevé (12,8%) et l'impôt réellement dû sur vos dividendes.
                </p>
              </div>

              <div className="help-section highlight-pfu">
                <h4>🔵 En régime PFU (Flat Tax)</h4>
                <p>
                  <strong>Régul IR = 0 €</strong>
                </p>
                <p>
                  L'acompte de 12,8% prélevé au versement constitue l'impôt définitif. Il n'y a aucune régularisation à faire.
                </p>
                <p className="help-note">
                  C'est le principal avantage du PFU : simplicité et prévisibilité.
                </p>
              </div>

              <div className="help-section highlight-bareme">
                <h4>🟠 En régime Barème progressif</h4>
                <p>
                  Vos dividendes (après abattement de 40%) sont ajoutés à <strong>TOUS vos autres revenus imposables</strong> de l'année pour calculer votre TMI (Taux Marginal d'Imposition).
                </p>
                <p>L'impôt sur dividendes = Dividendes × (1 - 40% abattement) × TMI</p>
                <p>Puis on déduit l'acompte de 12,8% déjà payé :</p>
                <ul>
                  <li><strong>TMI &lt; 12,8%</strong> → Crédit d'impôt (remboursement)</li>
                  <li><strong>TMI = 12,8%</strong> → Régul = 0 €</li>
                  <li><strong>TMI &gt; 12,8%</strong> → Complément à payer</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>⚠️ Important : impact global</h4>
                <p>
                  En barème progressif, le montant de la Régul IR dépend de <strong>l'ensemble de vos revenus</strong> :
                </p>
                <ul>
                  <li>Salaires, rémunération de dirigeant</li>
                  <li>Revenus fonciers, LMNP</li>
                  <li>Autres dividendes (toutes sociétés)</li>
                  <li>Etc.</li>
                </ul>
                <p className="help-note">
                  Plus vos revenus totaux sont élevés, plus votre TMI est élevé, et plus la Régul IR sera importante.
                </p>
              </div>

              <div className="help-section">
                <h4>💡 Comment choisir ?</h4>
                <table className="help-comparison-table">
                  <thead>
                    <tr>
                      <th>Votre situation</th>
                      <th>Régime conseillé</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>TMI ≤ 11%</td>
                      <td>Barème (moins taxé que PFU)</td>
                    </tr>
                    <tr>
                      <td>TMI = 30%</td>
                      <td>À calculer (proche du PFU)</td>
                    </tr>
                    <tr>
                      <td>TMI ≥ 41%</td>
                      <td>PFU (flat tax avantageuse)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonEditPage;
